/**
 * Initial Site Inspection Form — structured configuration derived from the
 * "V2 Build and Install Deployment Site Assessment" Google Form (10 sections).
 *
 * Answers are stored in `site_inspection_appointments.form_state` (JSONB),
 * keyed by field id. Value shapes by field type:
 *   text | textarea | date | time | radio | scale  -> string
 *   checkboxes                                      -> string[] (+ `${id}__other` string when hasOther)
 *   grid                                            -> Record<rowId, string>
 *   file                                            -> handled via attachment endpoints (not stored here)
 */

export type SiteInspectionFieldType =
  | 'text'
  | 'textarea'
  | 'date'
  | 'time'
  | 'radio'
  | 'checkboxes'
  | 'scale'
  | 'grid'
  | 'file';

export interface SiteInspectionField {
  id: string;
  label: string;
  type: SiteInspectionFieldType;
  required?: boolean;
  options?: string[];
  hasOther?: boolean;
  placeholder?: string;
  helpText?: string;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  gridRows?: { id: string; label: string }[];
  gridColumns?: string[];
}

export interface SiteInspectionSection {
  id: string;
  title: string;
  description?: string;
  fields: SiteInspectionField[];
}

export const SITE_INSPECTION_SECTIONS: SiteInspectionSection[] = [
  {
    id: 'section_intro',
    title: 'Site & Customer Details',
    description:
      'Evaluate the technical and logistical readiness of a site for a new system build and installation deployment.',
    fields: [
      { id: 'customer', label: 'Customer', type: 'text', required: true },
      { id: 'state_region', label: 'State / Region', type: 'text' },
      // The Job Code / Deployment ID used to be typed in here. It is now
      // derived from the client's region and client number and shown in its own
      // section at the top of the page.
      { id: 'site_location', label: 'Site Location (Full Address)', type: 'text' },
      {
        id: 'primary_contact',
        label: 'Primary On-Site Contact Name and Phone Number',
        type: 'text',
      },
    ],
  },
  {
    id: 'section_1',
    title: 'Section 1 — Access & Location',
    fields: [
      {
        id: 'accessibility',
        label: 'Accessibility: Is the site accessible during standard business hours (9 AM - 5 PM)?',
        type: 'radio',
        options: [
          'Yes, standard access',
          'No, requires special appointment/security clearance',
          '24/7 access',
        ],
      },
      { id: 'date_of_assessment', label: 'Date of Site Assessment', type: 'date' },
      {
        id: 'location_type',
        label: 'Location Type',
        type: 'radio',
        required: true,
        hasOther: true,
        options: ['Hotel', 'Restaurant', 'School', 'Sports Facility', 'Pharmaceutical'],
      },
      { id: 'section_1_notes', label: 'Section 1 Notes', type: 'textarea' },
    ],
  },
  {
    id: 'section_2',
    title: 'Section 2 — Unit Deployment / Install Types',
    fields: [
      {
        id: 'deployment_type',
        label: 'Deployment Type',
        type: 'radio',
        options: ['BIM-E', 'TIM-E', 'Kebb-i', 'RUN-R', 'PUR-E', 'LIM-E', 'Kebb-ai'],
      },
      {
        id: 'bin_type',
        label: 'Type of BINS',
        type: 'radio',
        options: ['Large Plastic', 'Small Plastic', 'Metal'],
      },
      { id: 'rack_platform_type', label: 'Type of Rack Platform', type: 'radio', options: ['Roller', 'Static'] },
      { id: 'section_2_notes', label: 'Section 2 Notes', type: 'textarea' },
    ],
  },
  {
    id: 'section_3',
    title: 'Section 3 — Location Install Parameters',
    fields: [
      {
        id: 'required_tools',
        label: 'Required Tools/Equipment Check (Select all that apply for installation)',
        type: 'checkboxes',
        hasOther: true,
        options: [
          'Ladders/Scaffolding required',
          'Forklift/Lift Equipment access',
          'Standard Hand Tools available',
          'Specialized electrical testing equipment',
          'Borescope / Endoscope',
          'Octopus Cellular scanner',
        ],
      },
      {
        id: 'building_construction_type',
        label: 'Building Construction Type',
        type: 'radio',
        options: ['Concrete Construction', 'Wood Construction', 'Steel Construction'],
      },
      {
        id: 'door_actuators_info',
        label: 'Door Actuators Info',
        type: 'radio',
        options: ['Single Door', 'Double Door'],
      },
    ],
  },
  {
    id: 'section_4',
    title: 'Section 4 — Door & Ceiling',
    fields: [
      { id: 'door_type', label: 'Door Type', type: 'radio', options: ['Glass', 'Wood', 'Steel'] },
      { id: 'door_frame_type', label: 'Door Frame Type', type: 'radio', options: ['Steel', 'Wood'] },
      {
        id: 'ceiling_type',
        label: 'Ceiling Type',
        type: 'radio',
        options: ['Drywall', 'Drywall w/ access hatch', 'Ceiling Tile', 'Warehouse (High Ceiling)'],
      },
    ],
  },
  {
    id: 'section_5',
    title: 'Section 5 — Door Strike & Power Outlets',
    fields: [
      {
        id: 'door_strike_type',
        label: 'Current Door Strike Type',
        type: 'radio',
        options: ['Full Lip', 'T-Style', 'D-Style'],
      },
      {
        id: 'strike_frame_material',
        label: 'Remove current strike plate — what type of material is the frame made out of?',
        type: 'radio',
        options: ['Metal', 'Wood'],
      },
      {
        id: 'nearby_power_outlets',
        label: 'Nearby Power Outlet(s)',
        type: 'radio',
        hasOther: true,
        options: ['In Ceiling', 'Exterior Wall Outlet', 'Power Junction Box'],
      },
    ],
  },
  {
    id: 'section_6',
    title: 'Section 6 — Power Requirements',
    fields: [
      {
        id: 'available_outlets_count',
        label: 'Power Requirements: Availability of dedicated circuits/outlets near the installation area (number of available outlets)',
        type: 'scale',
        scaleMin: 1,
        scaleMax: 5,
      },
      {
        id: 'tested_outlet',
        label: 'Tested Outlet',
        type: 'radio',
        options: ['Yes, has power', 'Inactive outlet'],
      },
      {
        id: 'estimated_power_distance',
        label: 'Estimated Power Distance',
        type: 'radio',
        options: ['Outlet to Ceiling', 'Outlet to drop point'],
      },
    ],
  },
  {
    id: 'section_7',
    title: 'Section 7 — Physical Space Readiness',
    fields: [
      {
        id: 'physical_space_readiness',
        label: 'Physical Space Readiness: Rate the following aspects of the installation area',
        type: 'grid',
        gridColumns: [
          'Poor/Inadequate (1)',
          'Needs Improvement (2)',
          'Acceptable (3)',
          'Good (4)',
          'Excellent (5)',
        ],
        gridRows: [
          { id: 'clear_floor_space', label: 'Adequate clear floor space for equipment placement' },
          { id: 'ceiling_height', label: 'Ceiling height and clearance for installation activities' },
          { id: 'clear_pathways', label: 'Clear pathways for equipment delivery (e.g., door width)' },
          { id: 'floor_angle', label: 'Floor angle in the installation zone' },
        ],
      },
    ],
  },
  {
    id: 'section_8',
    title: 'Section 8 — Connectivity, Environment & Photos',
    fields: [
      {
        id: 'cellular_connectivity',
        label: 'Cellular Network Connectivity Assessment',
        type: 'grid',
        gridColumns: ['Yes', 'No', 'N/A', 'Pending'],
        gridRows: [{ id: 'wifi_signal', label: 'WiFi signal strength adequate in installation zone' }],
      },
      {
        id: 'environmental_conditions',
        label:
          'Environmental Conditions: Does the site meet required temperature/humidity standards for the equipment?',
        type: 'checkboxes',
        options: [
          'Yes, within specified operating range',
          'No, environment is too hot/cold',
          'No, high humidity levels detected',
          'Not assessed',
        ],
      },
      {
        id: 'estimated_install_time',
        label: 'Estimated time needed for the physical installation (in HH:MM:SS)',
        type: 'text',
        placeholder: 'e.g. 4:03:32',
      },
      {
        id: 'site_photos',
        label: 'Upload Site Photos (Installation Area, Power Source, Network Drop)',
        type: 'file',
      },
    ],
  },
  {
    id: 'section_9',
    title: 'Section 9 — Overall Assessment',
    fields: [
      {
        id: 'overall_readiness_score',
        label: 'Overall Site Readiness Score for Deployment',
        type: 'scale',
        scaleMin: 1,
        scaleMax: 5,
      },
      {
        id: 'further_notes',
        label: 'Further Notes or Risks Identified During Assessment',
        type: 'textarea',
      },
    ],
  },
];

/** All non-file fields flattened, for validation / completeness checks. */
export function allSiteInspectionFields(): SiteInspectionField[] {
  return SITE_INSPECTION_SECTIONS.flatMap((s) => s.fields);
}
